/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Flex } from '@chakra-ui/react';
import { useForm, useFieldArray } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams, useLocation, useBlocker } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import useCustomToast from '@/components/common/Toast';
import CustomButton from '@/components/common/Button';
import {
  usePostProduct,
  useEditProduct,
  useGetCategories,
  useGetSizes,
  useGetProductDetail,
} from '@/services/addProduct';
import CategorySelectionSection from '@/components/AddProduct/CategorySelectionSection';
import ProductDetailsSection from '@/components/AddProduct/ProductDetailsSection';
import VariantDetailsSection from '@/components/AddProduct/VariantDetailsSection';
import { AxiosError } from 'axios';

import { WarningIcon } from '@chakra-ui/icons';
import LoadingComponent from '@/components/common/Loading';
import { useGetAddOnFields } from '@/services/addProduct';
import CustomizeSection from '@/components/AddProduct/CustomSection';
import { SIZE_GUIDE_IMAGES } from '@/utils/size-guide-path';
import { useQueryClient } from 'react-query';
import { useStore } from '@/store/store';
import { IProductEditResponse } from '@/services/manageProduct/types';
import { type FormData } from '@/services/addProduct/types';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';

const customSizeOptions = [
  { value: 'satin_petticoat' },
  { value: 'sari_belts' },
  { value: 'shapewear_petticoats' },
  { value: 'embroidered_sari_belts' },
];

const validationSchema = yup.object({
  categorySelection: yup
    .object({
      category: yup.string().required('Category is required'),
      subCategory: yup.string().required('Sub-Category is required'),
    })
    .required(),
  productDetails: yup
    .object({
      name: yup.string().required('Product name is required'),
      brand: yup.string().required('Brand is required'),
      collection: yup.string().optional(),
      discount: yup
        .number()
        .typeError('Discount percentage must be a valid number')
        .min(0, 'Discount percentage must be non-negative')
        .max(100, 'Discount percentage cannot exceed 100')
        .optional()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value)),
      stitchTime: yup
        .number()
        .typeError('Stitch Time must be a number')
        .required('Stitch Time is required')
        .integer('Stitch Time must be a whole number')
        .min(1, 'Stitch Time must be at least 1 day')
        .transform((value, originalValue) => (originalValue === '' ? undefined : value)),
      fabric: yup.string().required('Fabric is required'),
      shortDescription: yup.string().required('Short description is required'),
      video: yup.mixed().optional(),
      productSpecifications: yup.string().optional(),
      detailedProductDescription: yup.string().optional(),
      additionalInformation: yup.string().optional(),
      previewImage: yup.string().optional(),
    })
    .required('Product details are required'),
  variants: yup
    .array()
    .of(
      yup
        .object({
          color: yup
            .object({
              name: yup.string().required('Color name is required'),
              hex_code: yup.string().required('Color hex code is required'),
              id: yup.number().optional(),
            })
            .required('Color selection is required'),
          isAvailable: yup.boolean().optional(),
          productImage: yup
            .array()
            .of(
              yup.object({
                id: yup.number().required(),
                url: yup.string().optional(),
                file: yup.mixed().optional(),
              }),
            )
            .test(
              'min-2-images',
              'At least 2 product images are required for this variant',
              function (value) {
                if (!Array.isArray(value)) return false;
                const validImages = value.filter(
                  (img) =>
                    img &&
                    (img.file ||
                      (img.url && img.url !== '' && img.id !== 0) ||
                      (img.url && img.url.startsWith('blob:'))),
                );
                return validImages.length >= 2;
              },
            )
            .min(1, 'At least one image slot must be configured'),
          sizes: yup
            .array()
            .of(
              yup.object({
                id: yup.number().optional(),
                size: yup.string().required('Size is required'),
                variantPrice: yup
                  .number()
                  .transform((value, originalValue) => {
                    return originalValue === '' || originalValue === null ? undefined : value;
                  })
                  .typeError('Price must be a valid number')
                  .required('Price is required')
                  .min(0.01, 'Price must be greater than 0')
                  .test(
                    'max-two-decimals',
                    'Price can have at most 2 decimal places',
                    (value) => value === undefined || /^\d+(\.\d{1,2})?$/.test(String(value)),
                  ),
                stockQuantity: yup
                  .number()
                  .min(0, 'Stock quantity must be 0 or more')
                  .typeError('Stock quantity must be a number')
                  .required('Stock quantity is required'),
                weight: yup
                  .string()
                  .required('Weight is required')
                  .matches(
                    /^\d+(\.\d{0,2})?$/,
                    'Weight must be a valid decimal with up to 2 decimal places',
                  )
                  .test(
                    'weight-greater-than-zero',
                    'Weight must be greater than 0',
                    (value) => Number(value || 0) > 0,
                  ),
                isPrimary: yup.boolean().optional(),
                sku: yup.string().optional(),
              }),
            )
            .min(1, 'At least one size is required for this variant'),
        })
        .required('Variant configuration is required'),
    )
    .min(1, 'At least one variant is required'),
  is_customizable: yup.boolean().required(),
  customized_price: yup.string().when('is_customizable', {
    is: true,
    then: (schema) =>
      schema
        .required('Custom price is required when customization is enabled')
        .matches(/^-?\d+(\.\d{1,2})?$/, 'Custom price must have at most 2 decimal places')
        .test(
          'non-negative',
          'Custom price cannot be negative',
          (value) => value != null && value !== '' && Number(value) >= 0,
        ),
    otherwise: (schema) => schema.optional(),
  }),
  customized_weight: yup
    .string()
    .matches(/^\d+(\.\d{0,2})?$/, 'Weight must be a valid decimal with up to 2 decimal places')
    .when(['is_customizable', 'measurement_fields'], {
      is: (is_customizable: boolean, measurement_fields: number[]) =>
        is_customizable && (measurement_fields?.length ?? 0) > 0,
      then: (schema) =>
        schema
          .required('Customized weight is required when style options are selected')
          .test(
            'greater-than-zero',
            'Weight must be greater than 0 when style options are selected',
            (value) => {
              if (!value || value === '0' || value === '0.00') return false;
              return Number(value) > 0;
            },
          ),
      otherwise: (schema) => schema.optional().nullable(),
    }),

  ...Object.fromEntries(
    customSizeOptions
      .map((option) => [
        [`custom_size_${option.value}`, yup.boolean().optional()],
        [`custom_size_price_${option.value}`, yup.string().optional()],
      ])
      .flat(),
  ),
}) as yup.ObjectSchema<FormData>;

const AddProduct = () => {
  const navigate = useNavigate();
  const { editProductId: id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useCustomToast();
  const {
    data: product,
    isLoading,
    refetch,
  } = useGetProductDetail(id ?? '') as {
    data: IProductEditResponse | undefined;
    isLoading: boolean;
    refetch: () => void;
  };
  const { data: addOnFields = [], isLoading: addOnFieldsLoading } = useGetAddOnFields();
  const { mutateAsync: addProduct } = usePostProduct();
  const { mutateAsync: editProduct } = useEditProduct(id ?? '');
  const [renderKey, setRenderKey] = useState(0);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [, setNextLocation] = useState<string | null>(null);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const { user } = useStore();
  const vendorCurrency = user?.currency || 'NPR';
  const { data: sizes = [] } = useGetSizes();
  const fromTab = location.state?.fromTab || 0;
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    setError,
    clearErrors,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      categorySelection: { category: '', subCategory: '' },
      productDetails: {
        name: '',
        brand: '',
        collection: '',
        discount: undefined,
        fabric: '',
        shortDescription: '',
        video: undefined,
        productSpecifications: '',
        detailedProductDescription: '',
        additionalInformation: '',
        stitchTime: undefined,
      },
      variants: [
        {
          color: { name: '', hex_code: '', id: 0 },
          isAvailable: false,
          productImage: [],
          sizes: [
            {
              id: 0,
              size: '',
              variantPrice: 0,
              stockQuantity: 0,
              weight: '0.00',
              isPrimary: false,
              sku: '',
            },
          ],
        },
      ],
      is_customizable: false,
      customized_price: '0',
      customized_weight: '0.00',
      measurement_fields: [],
      is_add_on: false,
    },
  });

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (isSubmittingForm) return false;
    if (isLeavingConfirmed) return false;
    if (isIntentionalLeave) return false;

    return isFormDirty && currentLocation.pathname !== nextLocation.pathname;
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'variants',
  });

  const selectedCategoryId = watch('categorySelection.category');
  const { data: categories } = useGetCategories();
  const selectedCategory = categories?.find((cat: any) => cat.id.toString() === selectedCategoryId);
  const selectedCategoryName = selectedCategory?.name || '';

  const firstCategoryImageFromBackend = useMemo(() => {
    if (!selectedCategoryName) return undefined;
    const slug = selectedCategoryName
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    const images = SIZE_GUIDE_IMAGES[slug as keyof typeof SIZE_GUIDE_IMAGES];
    return images?.[0];
  }, [selectedCategoryName]);

  useEffect(() => {
    const element = document.querySelector('.chakra-button.css-trj4gu') as HTMLElement;
    if (element) element.style.display = 'none';
  }, []);

  const [hasInitializedEditData, setHasInitializedEditData] = useState(false);
  const [isLeavingConfirmed, setIsLeavingConfirmed] = useState(false);
  const [isIntentionalLeave, setIsIntentionalLeave] = useState(false);
  const blockHandledRef = useRef(false);
  useEffect(() => {
    if (blocker.state === 'blocked' && !blockHandledRef.current) {
      blockHandledRef.current = true;
      setShowLeaveModal(true);
      setNextLocation(blocker.location?.pathname + (blocker.location?.search || ''));
    }

    if (blocker.state === 'unblocked') {
      blockHandledRef.current = false;
      setShowLeaveModal(false);
      setNextLocation(null);
    }
  }, [blocker.state, blocker.location]);
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    if (isFormDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isFormDirty]);

  useEffect(() => {
    if (!id && hasInitializedEditData) {
      reset({
        categorySelection: { category: '', subCategory: '' },
        productDetails: {
          name: '',
          brand: '',
          collection: '',
          discount: undefined,
          fabric: '',
          shortDescription: '',
          video: undefined,
          productSpecifications: '',
          detailedProductDescription: '',
          additionalInformation: '',
          stitchTime: 2,
        },
        variants: [
          {
            color: { name: '', hex_code: '', id: 0 },
            isAvailable: false,
            productImage: [],
            sizes: [
              {
                id: 0,
                size: '',
                variantPrice: 0,
                stockQuantity: 0,
                weight: '0.00',
                isPrimary: false,
                sku: '',
              },
            ],
          },
        ],
        is_customizable: false,
        customized_price: '0',
        customized_weight: '0.00',
        measurement_fields: [],
        is_add_on: false,
        ...Object.fromEntries(
          customSizeOptions.flatMap((option) => [
            [`custom_size_${option.value}`, false],
            [`custom_size_price_${option.value}`, ''],
          ]),
        ),
      });
      setHasInitializedEditData(false);
    }
  }, [id, reset]);

  useEffect(() => {
    if (id && product && !addOnFieldsLoading && !hasInitializedEditData && sizes.length > 0) {
      const variantMap = new Map();

      (product.product_variants || []).forEach((variant: any) => {
        const colorKey = `${variant.color || ''}_${variant.value || ''}`;

        let colorId = 0;
        if (variant.color_id) {
          colorId = Number(variant.color_id);
        } else if (variant.color?.id) {
          colorId = Number(variant.color.id);
        } else if (variant.color_variant_id) {
          colorId = Number(variant.color_variant_id);
        }

        if (!variantMap.has(colorKey)) {
          variantMap.set(colorKey, {
            groupId: variant.id || 0,
            color: {
              id: colorId,
              name: variant.color || '',
              hex_code: variant.value || '',
            },
            isAvailable: variant.is_available ?? true,
            productImage: Array(4)
              .fill(null)
              .map(() => ({ id: 0, url: '', file: undefined })),
            sizes: [],
          });
        }

        const entry = variantMap.get(colorKey);

        (variant.images || []).forEach((img: any, idx: number) => {
          if (idx < 4) {
            entry.productImage[idx] = {
              id: Number(img.id) || 0,
              url: img.image || img.image_medium || img.image_small || '',
              file: undefined,
            };
          }
        });

        entry.sizes.push({
          id: Number(variant.id) || 0,
          size: String(variant.size?.id || variant.size || ''),
          variantPrice: Number(variant.variant_price) || 0,
          stockQuantity: Number(variant.stock_quantity) || 0,
          weight: String(variant.variant_weight || '0.00'),
          isPrimary: Boolean(variant.is_primary),
          sku: variant.sku || '',
        });
      });

      const variants = Array.from(variantMap.values()).map((entry) => ({
        color: entry.color,
        isAvailable: entry.isAvailable,
        productImage: entry.productImage,
        sizes: entry.sizes,
      }));

      const hasCustomSizeOnly = (product.product_variants || []).some((v: any) =>
        sizes.some(
          (s: any) =>
            String(s.id) === String(v.size?.id || v.size) && s.value === 'Custom Size Only',
        ),
      );

      reset({
        categorySelection: {
          category: String(product.category?.id || ''),
          subCategory: String(product.sub_category?.id || ''),
        },
        productDetails: {
          name: product.name || '',
          brand: String(product.brand?.id || ''),
          collection: product.collection ? String(product.collection.id) : undefined,
          discount: product.discount_percent ? Number(product.discount_percent) : undefined,
          stitchTime: product.preparation_days ? Number(product.preparation_days) : undefined,
          fabric: String(product.fabric?.id || ''),
          shortDescription: product.description || '',
          video: product.video || undefined,
          productSpecifications: product.product_specification || '',
          detailedProductDescription: product.detailed_product_description || '',
          additionalInformation: product.additional_information || '',
          previewImage: product.size_image || firstCategoryImageFromBackend || '',
        },
        variants: variants.length > 0 ? variants : [],
        is_customizable:
          Boolean(product.is_customizable) ||
          (product.style_fields && product.style_fields.length > 0) ||
          hasCustomSizeOnly,
        customized_price: String(product.customized_price || '0.00'),
        customized_weight: String(product.customized_weight || '0.00'),
        measurement_fields: product.style_fields?.map((sf: any) => sf.id) || [],
        is_add_on: Boolean(product.is_add_on),
      });

      setHasInitializedEditData(true);
    }
  }, [
    id,
    product,
    addOnFieldsLoading,
    hasInitializedEditData,
    sizes,
    addOnFields,
    reset,
    firstCategoryImageFromBackend,
  ]);
  useEffect(() => {
    const subscription = watch(() => {
      setIsFormDirty(true);
    });

    return () => subscription.unsubscribe();
  }, [watch]);
  const handleReset = () => {
    reset({
      categorySelection: { category: '', subCategory: '' },
      productDetails: {
        name: '',
        brand: '',
        collection: '',
        discount: undefined,
        fabric: '',
        shortDescription: '',
        video: undefined,
        productSpecifications: '',
        detailedProductDescription: '',
        additionalInformation: '',
      },
      variants: [
        {
          color: { name: '', hex_code: '' },
          isAvailable: false,
          productImage: [],
          sizes: [
            {
              id: 0,
              size: '',
              variantPrice: 0,
              stockQuantity: 0,
              weight: '0.00',
              isPrimary: false,
            },
          ],
        },
      ],
      is_customizable: false,
      customized_price: '0',
      customized_weight: '0.00',
      measurement_fields: [],
      is_add_on: false,
      ...Object.fromEntries(
        customSizeOptions
          .map((option) => [
            [`custom_size_${option.value}`, false],
            [`custom_size_price_${option.value}`, ''],
          ])
          .flat(),
      ),
    });

    setIsFormDirty(false);
    setIsLeavingConfirmed(false);
    setRenderKey((prev) => prev + 1);

    // Signal intentional leave
    setIsIntentionalLeave(true);

    // Defer navigation until next microtask → state has time to update
    setTimeout(() => {
      navigate('/manage-product', { state: { tabIndex: fromTab || 0 } });
    }, 0);
  };
  useEffect(() => {
    return () => {
      // Cleanup when leaving the page
      setIsLeavingConfirmed(false);
      setIsFormDirty(false);
    };
  }, []);
  useEffect(() => {
    return () => {
      setIsIntentionalLeave(false);
      setIsLeavingConfirmed(false);
      setIsFormDirty(false);
    };
  }, []);
  const onSubmit = async (data: FormData) => {
    setIsSubmittingForm(true);
    if (!data.productDetails) {
      showToast('error', 'Product details are missing. Please refresh and try again.');
      return;
    }

    const formData = new FormData();
    const isEdit = !!id;

    formData.append('name', data.productDetails.name);
    formData.append('category_id', data.categorySelection.category);
    formData.append('sub_category_id', data.categorySelection.subCategory);
    formData.append('brand_id', data.productDetails.brand);
    formData.append('fabric_id', data.productDetails.fabric);
    formData.append('description', data.productDetails.shortDescription);
    formData.append('discount_percent', String(data.productDetails.discount ?? 0));
    formData.append('preparation_days', String(data.productDetails.stitchTime ?? ''));
    formData.append('product_specification', data.productDetails.productSpecifications ?? '');
    formData.append(
      'detailed_product_description',
      data.productDetails.detailedProductDescription ?? '',
    );
    formData.append('additional_information', data.productDetails.additionalInformation ?? '');

    if (data.productDetails.collection) {
      formData.append('collection_id', data.productDetails.collection);
    } else if (isEdit) {
      formData.append('collection_id', '');
    }

    if (data.productDetails.video instanceof File) {
      const safeVideoName = `video-${Date.now()}.mp4`;
      const safeVideo = new File([data.productDetails.video], safeVideoName, {
        type: data.productDetails.video.type || 'video/mp4',
      });
      formData.append('video', safeVideo);
    } else if (isEdit && data.productDetails.video === '' && product?.video) {
      formData.append('video', '');
    }

    const currentPreviewImage = data.productDetails.previewImage?.trim();
    const originalSizeImage = product?.size_image?.trim();

    const isManualUpload =
      currentPreviewImage &&
      (currentPreviewImage.startsWith('data:') || currentPreviewImage.startsWith('blob:'));

    const isPredefinedUrl =
      currentPreviewImage && currentPreviewImage.startsWith('http') && !isManualUpload;

    const isUnchanged = currentPreviewImage === originalSizeImage;
    const isRemoved = !currentPreviewImage && originalSizeImage;

    if (isUnchanged) {
      // Size guide image unchanged → no action needed
    } else if (isManualUpload) {
      try {
        const response = await fetch(currentPreviewImage);
        if (!response.ok) throw new Error('Failed to fetch uploaded image');
        const blob = await response.blob();
        const extension = blob.type.split('/')[1] || 'png';
        const fileName = `size-guide-${Date.now()}.${extension}`;
        const file = new File([blob], fileName, { type: blob.type });
        formData.append('size_image', file);
      } catch (err) {
        console.error('Failed to process manual size guide upload:', err);
        showToast('error', 'Failed to process size guide image');
        return;
      }
    } else if (isPredefinedUrl) {
      try {
        const response = await fetch(currentPreviewImage);
        if (!response.ok) throw new Error(`Failed to fetch predefined image: ${response.status}`);
        const blob = await response.blob();
        const extension = blob.type.split('/')[1] || 'png';
        const fileName = `size-guide-${Date.now()}.${extension}`;
        const file = new File([blob], fileName, { type: blob.type });
        formData.append('size_image', file);
      } catch (err) {
        console.error('Failed to fetch predefined size guide:', err);
        showToast(
          'error',
          'Failed to download selected size guide image. Please try uploading manually.',
        );
        return;
      }
    } else if (isRemoved) {
      formData.append('size_image', '');
    } else {
      console.warn('Unexpected size guide state:', {
        currentPreviewImage,
        originalSizeImage,
      });
    }

    const hasCustomSizeOnly = data.variants.some((v) =>
      v.sizes.some(
        (s: { size: string }) =>
          sizes.find((sz: any) => String(sz.id) === s.size)?.value === 'Custom Size Only',
      ),
    );
    const shouldBeCustomizable = data.is_customizable || hasCustomSizeOnly;

    formData.append('is_customizable', shouldBeCustomizable ? 'true' : 'false');
    if (shouldBeCustomizable) {
      formData.append('customized_price', String(data.customized_price || 0));
      formData.append('customized_weight', data.customized_weight || '0.00');
      data.measurement_fields?.forEach((sid, i) => {
        formData.append(`style_field_ids[${i}]`, String(sid));
      });
    }

    formData.append('is_add_on', 'false');

    const processedVariants = new Map<string, any>();

    data.variants.forEach((variant) => {
      if (!variant.color?.name || !variant.color?.hex_code) return;

      const colorKey = `${variant.color.name}_${variant.color.hex_code}`;
      if (!processedVariants.has(colorKey)) {
        processedVariants.set(colorKey, {
          color: variant.color,
          sizes: [...variant.sizes],
          images: [...variant.productImage],
        });
      } else {
        const existing = processedVariants.get(colorKey);
        existing.sizes.push(...variant.sizes);
      }
    });

    let colorIndex = 0;
    for (const [, variantInfo] of processedVariants.entries()) {
      const { color, sizes, images } = variantInfo;

      formData.append(`product_variant[${colorIndex}][color]`, color.name);
      formData.append(`product_variant[${colorIndex}][value]`, color.hex_code);

      if (isEdit && color.id && color.id !== 0) {
        formData.append(`product_variant[${colorIndex}][color_id]`, String(color.id));
      }

      sizes.forEach((size: any, sIdx: number) => {
        if (size.id > 0) {
          formData.append(`product_variant[${colorIndex}][variants][${sIdx}][id]`, String(size.id));
        }
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][size_id]`,
          String(size.size),
        );
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][variant_price]`,
          String(size.variantPrice),
        );
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][variant_weight]`,
          String(size.weight || '0.00'),
        );
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][stock_quantity]`,
          String(size.stockQuantity),
        );
        formData.append(`product_variant[${colorIndex}][variants][${sIdx}][sku]`, size.sku ?? '');
        formData.append(`product_variant[${colorIndex}][variants][${sIdx}][is_available]`, 'true');
        formData.append(`product_variant[${colorIndex}][variants][${sIdx}][is_active]`, 'true');
        formData.append(
          `product_variant[${colorIndex}][variants][${sIdx}][is_primary]`,
          String(size.isPrimary ?? false),
        );

        if (sIdx === 0) {
          let imgIdx = 0;

          images.forEach((img: any) => {
            if (!img || (!img.file && !img.url && img.id === 0)) return;

            const isNewUpload = img.file instanceof File;

            if (isNewUpload) {
              const safeName = `vimg-${colorIndex}-${sIdx}-${imgIdx}-${Date.now()}.jpg`;
              const safeFile = new File([img.file], safeName, {
                type: img.file.type || 'image/jpeg',
              });

              formData.append(
                `product_variant[${colorIndex}][variants][${sIdx}][images][${imgIdx}][image]`,
                safeFile,
              );

              formData.append(
                `product_variant[${colorIndex}][variants][${sIdx}][images][${imgIdx}][is_primary]`,
                imgIdx === 0 ? 'true' : 'false',
              );

              imgIdx++;
            }
          });
        }
      });

      colorIndex++;
    }

    try {
      const response = isEdit
        ? await editProduct(formData, id as any)
        : await addProduct(formData, {} as any);

      showToast('success', response?.message || 'Product saved successfully');

      await queryClient.invalidateQueries({ queryKey: ['product', id] });
      await refetch();
      setHasInitializedEditData(false);

      setIsFormDirty(false);

      navigate('/manage-product', { state: { tabIndex: id ? fromTab : 2 } });
    } catch (error: any) {
      console.error('API Error:', error);

      if (error instanceof AxiosError) {
        const responseData = error?.response?.data;

        if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
          const fieldErrors: string[] = [];

          Object.entries(responseData).forEach(([messages]) => {
            if (Array.isArray(messages)) {
              messages.forEach((msg) => fieldErrors.push(msg));
            } else if (typeof messages === 'string') {
              fieldErrors.push(messages);
            }
          });

          if (fieldErrors.length > 0) {
            showToast('error', fieldErrors.join('\n'));
            return;
          }
        }

        const backendErrorMessage =
          Array.isArray(responseData) && responseData?.[0]
            ? responseData[0]
            : responseData?.message || responseData?.detail || 'An error occurred';
        showToast('error', backendErrorMessage);
      } else {
        showToast('error', 'Failed to submit the form');
      }
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const onInvalid = (errors: any) => {
    console.error('VALIDATION FAILED', errors);
    showToast('error', 'Please fix the highlighted errors before submitting');
  };

  if (isLoading && id) {
    return <LoadingComponent />;
  }

  return (
    <Box id="add-product-box" fontFamily={'primary'} key={renderKey} p={{ base: 4, md: 6 }}>
      <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <Flex direction="column" gap={5}>
          <CategorySelectionSection control={control} errors={errors} id={id} />
          <ProductDetailsSection
            control={control}
            errors={errors}
            product={product ? { ...product, video: product.video ?? undefined } : undefined}
            setValue={setValue}
            clearErrors={clearErrors}
            setError={setError}
            categories={categories}
            defaultCategoryImage={firstCategoryImageFromBackend}
          />
          <VariantDetailsSection
            control={control}
            errors={errors}
            fields={fields}
            append={append}
            remove={remove}
            update={update}
            watch={watch}
            product={product ? { ...product, video: product.video ?? undefined } : undefined}
            id={id}
            showToast={showToast}
            setValue={setValue}
            vendorCurrency={vendorCurrency}
          />
          {selectedCategoryId && (
            <CustomizeSection
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
              clearErrors={clearErrors}
              trigger={trigger}
              product={product}
              id={id}
            />
          )}
          <Flex gap={2} justify={'space-between'}>
            <CustomButton onClick={handleReset} border={'1px solid'} variant="outline">
              Cancel
            </CustomButton>
            <CustomButton isLoading={isSubmitting} type="submit" mr={{ base: 16, lg: 36 }}>
              Submit
            </CustomButton>
          </Flex>
        </Flex>
      </form>
      <CustomConfirmationModal
        isOpen={showLeaveModal}
        onClose={() => {
          blockHandledRef.current = false; // Reset the ref
          setShowLeaveModal(false);
          setNextLocation(null);
          setIsLeavingConfirmed(false);

          if (blocker.state === 'blocked') {
            blocker.reset?.();
          }
        }}
        onConfirm={() => {
          blockHandledRef.current = false; // Reset the ref
          setShowLeaveModal(false);
          setIsLeavingConfirmed(true);

          if (blocker.state === 'blocked') {
            blocker.proceed?.();

            setTimeout(() => {
              setIsFormDirty(false);
              setNextLocation(null);
            }, 0);
          }
        }}
        icon={WarningIcon}
        title="Unsaved Changes"
        description={
          <>
            You have unsaved changes.
            <br />
            Leaving this page will discard them.
          </>
        }
        buttonText="Leave Anyway"
      />
    </Box>
  );
};

export default AddProduct;
